import { getHandleTextForSearch } from "./user";
import { normalizeAndLowerCase } from "./string";
import User from "../api/model/users/User";

const BLOCK_SEARCH_THRESHOLD = 3;

/**
 * Calculates matching score and fuzziness of target against searchTerm
 */
function calculateMatchingScore({
    searchTerm,
    target,
    additionalTarget,
    enableFuzzySearch,
    enableWordSearch
}: {
    searchTerm: string;
    target: string;
    additionalTarget?: string;
    enableFuzzySearch?: boolean;
    enableWordSearch?: boolean;
}) {
    const targetLower = target.toLowerCase();

    // Exact match: return highest score
    if (searchTerm === targetLower) {
        return 1;
    }

    let score = 0;
    let targetSearchTerm = searchTerm.toLowerCase();
    let additionalScore = 0;
    const additionalTargetLower = additionalTarget && additionalTarget.toLowerCase();
    const additionalTargetRegex = new RegExp(`\\s*\\b${additionalTargetLower}\\b\\s*`, "ig");
    // `additionalTarget` match: add big score and remove additionalTarget from search term if it is a standalone word
    if (additionalTargetLower && additionalTargetRegex.test(targetSearchTerm)) {
        additionalScore = 0.3;

        targetSearchTerm = targetSearchTerm.replace(additionalTargetRegex, " ").trim();

        if (!targetSearchTerm) {
            return additionalScore;
        }
    }

    if (enableWordSearch) {
        score += getWordSearchScore(targetLower, targetSearchTerm);
    } else {
        const index = targetLower.indexOf(targetSearchTerm);
        // Whole match: add big score if target starts with searched term, otherwise add small score
        if (index > -1) {
            score += 0.3;

            if (targetLower.startsWith(targetSearchTerm)) {
                score += 0.3;
            } else if (targetLower.charAt(index - 1) === " ") {
                score += 0.2;
            }

            // Add a small amount to the score for occurence place of the match in the target
            // (nearer to beginning is better)
            score += (targetLower.length - index) / targetLower.length / 200;
            return score + additionalScore;
        }

        if (enableFuzzySearch) {
            score += getFuzzyMatchingScore(targetLower, targetSearchTerm);
        }
    }

    if (!score) {
        return 0;
    }

    return score + additionalScore;
}

function getWordSearchScore(target: string, searchTerm: string) {
    const searchTermArray = searchTerm.split(" ");
    const targetArray = target.split(" ");
    const searchFn = searchTerm.length >= BLOCK_SEARCH_THRESHOLD ? "includes" : "startsWith";
    let targetIncludesEveryWord = true;
    let score = 0;

    for (const searchTermWord of searchTermArray) {
        const searchTermWordIndex = targetArray.findIndex(targetWord => targetWord[searchFn](searchTermWord));

        targetArray.splice(0, searchTermWordIndex + 1);

        if (searchTermWordIndex === -1) {
            targetIncludesEveryWord = false;
        }
    }

    if (targetIncludesEveryWord) {
        score = 0.3;
    }

    return score;
}

function getFuzzyMatchingScore(target: string, searchTerm: string) {
    let prvFoundIndex = target.indexOf(searchTerm.charAt(0));
    let score = 0;
    let searchIndex = 0;

    // Prepare score for fuzzy match
    for (let i = prvFoundIndex; i < target.length; i++) {
        if (prvFoundIndex === -1) {
            break;
        }

        // Again nerarer is better
        score += (target.length - prvFoundIndex) / target.length / 200;

        // Word starting is more than better
        if (target.charAt(prvFoundIndex > 0 ? prvFoundIndex - 1 : prvFoundIndex) === " ") {
            score += (target.length - prvFoundIndex) / target.length / 200;
        }

        const nextChar = searchTerm[++searchIndex];
        prvFoundIndex = nextChar ? target.indexOf(nextChar, prvFoundIndex + 1) : -1;
    }

    // Add fuzzy scores if there is a fuzzy match
    if (searchIndex === searchTerm.length) {
        return score + 0.05;
    }

    return 0;
}

function matchUserWithFilterValue(user: User, filterValue: string) {
    const { email, username, fullName } = user;
    const handleText = getHandleTextForSearch(fullName, username);

    return calculateMatchingScore({
        searchTerm: normalizeAndLowerCase(filterValue),
        target: normalizeAndLowerCase(handleText)
    }) || calculateMatchingScore({
        searchTerm: filterValue,
        target: email!
    });
}

export {
    calculateMatchingScore,
    matchUserWithFilterValue,
    getWordSearchScore
};
